"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Reviews() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const testimonials = [{
    quote: "BEMAX provided a flawless experience. The chauffeur was punctual, professional, and the vehicle was pristine.",
    name: "James W.",
    location: "MANHATTAN, NY",
    blend: "\n",
    rating: 5,
    serviceCode: "CH-01"
  }, {
    quote: "The most reliable service in the city. BEMAX makes every airport transfer feel like a private terminal experience.",
    name: "Sarah L.",
    location: "GREENWICH, CT",
    blend: "\n",
    rating: 5,
    serviceCode: "CH-02"
  }, {
    quote: "Exceptional attention to detail. BEMAX is my only choice for high-stakes corporate events and client transport.",
    name: "Robert D.",
    location: "STAMFORD, CT",
    blend: "\n",
    rating: 5,
    serviceCode: "CH-03"
  }, {
    quote: "Seamless booking and impeccable service. BEMAX sets the standard for luxury chauffeured transportation.",
    name: "Elena V.",
    location: "WESTPORT, CT",
    blend: "PRIVATE EVENT SERVICE",
    rating: 5,
    serviceCode: "CH-04"
  }, {
    quote: "The level of care and discretion is unmatched. BEMAX is truly the gold standard for private travel.",
    name: "Marcus T.",
    location: "NEW YORK, NY",
    blend: "VIP CHAUFFEUR SERVICE",
    rating: 5,
    serviceCode: "CH-05"
  }, {
    quote: "Reliable, elegant, and always on time. BEMAX makes navigating the city effortless and refined.",
    name: "Alice G.",
    location: "RYE, NY",
    blend: "CITY TRANSPORT SERVICE",
    rating: 5,
    serviceCode: "CH-06"
  }];
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);
  const pad = (n: number) => String(n).padStart(2, "0");
  return <section data-section-id="465" id="googlereviewstrustedleaders" className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8 lg:px-12 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Editorial Section Header */}
        <div ref={headerRef} className={["max-w-3xl mb-14 md:mb-20 transition-all duration-700 ease-out", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-foreground/40" />
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
              Client Testimonials
            </p>
          </div>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[48px] leading-[1.05] tracking-tight text-foreground mb-6">Trusted by the executive.</h2>
          <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">Experience the BEMAX standard. Read firsthand accounts from clients who rely on our commitment to punctuality, complete discretion, and refined premium transportation.&nbsp;</p>
        </div>

        {/* Carousel Slider */}
        <Carousel setApi={setApi} opts={{
        align: "start",
        loop: true,
        dragFree: false
      }} className="w-full">
          <CarouselContent className="-ml-6">
            {testimonials.map((t, i) => <CarouselItem data-index={i} key={i} className="pl-6 basis-full md:basis-1/2 lg:basis-1/3">
                <article className="relative group h-full rounded-lg border border-border/80 bg-card p-8 md:p-9 flex flex-col justify-between transition-all duration-300 hover:border-foreground/40 shadow-sm hover:shadow-md">
                  {/* Subtle top silver accent line */}
                  <div className="absolute top-0 left-6 right-6 h-[1.5px] bg-gradient-to-r from-transparent via-border to-transparent group-hover:via-foreground/30 transition-colors duration-500" />

                  {/* Header / Rating & Quote Icon */}
                  <div>
                    <div className="flex items-center justify-between gap-4 mb-6">
                      {/* 5-Star Metallic Rating */}
                      <div className="flex items-center gap-1.5" aria-label={`Rating: ${t.rating} out of 5 stars`}>
                        {Array.from({
                      length: t.rating
                    }).map((_, s) => <Star key={s} className="size-3.5 fill-foreground text-foreground opacity-90 transition-transform duration-300 group-hover:scale-105" strokeWidth={1.5} />)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] tracking-widest text-muted-foreground/70 uppercase">
                          {t.serviceCode}
                        </span>
                        <Quote className="size-4 text-muted-foreground/40 shrink-0" strokeWidth={1.5} />
                      </div>
                    </div>

                    {/* Editorial Testimonial Quote */}
                    <blockquote className="font-serif italic text-lg md:text-xl text-foreground/95 leading-relaxed tracking-normal mb-8">
                      &ldquo;{t.quote}&rdquo;
                    </blockquote>
                  </div>

                  {/* Client Info Footer */}
                  <div className="pt-6 border-t border-border/70 flex flex-col gap-1.5">
                    <p className="font-sans text-sm font-semibold tracking-wider text-foreground uppercase">
                      {t.name}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] tracking-[0.16em] uppercase text-muted-foreground">
                      <span>{t.location}</span>
                      <span className="text-border" aria-hidden="true">&bull;</span>
                      <span className="text-muted-foreground/90">{t.blend}</span>
                    </div>
                  </div>
                </article>
              </CarouselItem>)}
          </CarouselContent>

          {/* Minimalist Carousel Controls & Index Counter */}
          <div className="mt-12 md:mt-14 flex items-center justify-between gap-6 pt-6 border-t border-border/40">
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.25em] text-muted-foreground">
              <span className="text-foreground font-semibold">{pad(current + 1)}</span>
              <span className="text-border">/</span>
              <span>{pad(count || testimonials.length)}</span>
            </div>

            <div className="flex items-center gap-2.5">
              <Button type="button" variant="outline" size="icon" aria-label="Previous testimonial" onClick={() => api?.scrollPrev()} className="size-11 rounded-lg border-border/80 bg-background hover:bg-accent hover:border-foreground/40 hover:text-accent-foreground transition-all duration-200 active:scale-95">
                <ArrowLeft className="size-4" strokeWidth={1.5} />
              </Button>
              <Button type="button" variant="outline" size="icon" aria-label="Next testimonial" onClick={() => api?.scrollNext()} className="size-11 rounded-lg border-border/80 bg-background hover:bg-accent hover:border-foreground/40 hover:text-accent-foreground transition-all duration-200 active:scale-95">
                <ArrowRight className="size-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </Carousel>
      </div>
    </section>;
}